import Vue from "vue"
import VueRouter from "vue-router"
import store from "@/store/index.js"

// Router Parts
import policies from "@/modules/policies/routes/"
import auth from "@/modules/auth/routes/"
import dashboard from "@/modules/dashboard/routes/"
import users from "@/modules/users/routes/"
import internals from "@/modules/internals/routes/"
import setup from "@/modules/setup/routes/"
import commissions from "@/modules/commissions/routes/"
import claims from "@/modules/claims/routes/"
import leads from "@/modules/leads/routes/"
import products from "@/modules/products/routes/"
import notifications from "@/modules/notifications/routes/"
import reports from "@/modules/reports/routes/"
import quotes from "@/modules/quotes/routes/"
import organizations from "@/modules/organizations/routes"
import clients from "@/modules/clients/routes"
import templates from "@/modules/templates/routes"
import caseManagement from "@/modules/caseManagement/routes"
import binders from "@/modules/binders/routes"
import objects from "@/modules/objects/routes"
import fileSystem from "@/modules/fileSystem/routes"
import transactions from "@/modules/transactions/routes"
import plans from "@/modules/plans/routes"
import education from "@/modules/education/routes"
import achievements from "@/modules/achievements/routes"
import resources from "@/modules/resources/routes"

// Error
import PageError from "../pages/Error/"

Vue.use(VueRouter)

const router = new VueRouter({
	mode: "history",
	routes: [
		...dashboard,
		...clients,
		...leads,
		...policies,
		...quotes,
		...products,
		...auth,
		...users,
		...internals,
		...commissions,
		...claims,
		...notifications,
		...reports,
		...setup,
		...organizations,
		...templates,
		...caseManagement,
		...binders,
		...objects,
		...fileSystem,
		...transactions,
		...plans,
		...education,
		...achievements,
		...resources,

		// If route doesn't exist, show error here that this URL doesn't exist
		{
			path: "*",
			component: PageError
		}
	],

	// Always scroll to top except when back/forward button of browser is used
	scrollBehavior(to, from, savedPosition) {
		if (savedPosition) {
			return savedPosition
		} else {
			return { x: 0, y: 0 }
		}
	}
})

// Global guard for Auth + init of a client in case he is logged in
router.beforeEach(async (to, from, next) => {
	// Always show new-password page, doesn't matter if user is logged in
	if (to.name === "new-password") {
		// Remove token and continue
		window.TK = null
		next()
	} else if (
		to.name === "login" ||
		to.name === "new-password" ||
		to.name === "reset-password" ||
		to.name === "forgotten-password"
	) {
		// If client is logged in, redirect to clients, if not continue
		if (window.TK) next(`/${to.params.tenantId}/clients`)
		else next()
	} else {
		// If client is logged in, just continue, if not, redirect to login
		if (window.TK) {
			// Init tenant if it hasn't been done yet
			if (to.params.tenantId)
				store.commit("UPDATE_TENANT_ID", to.params.tenantId)
			if (!store.state.settings || !store.state.permissions)
				store
					.dispatch("init")
					.then(() => next())
					.catch(err => {
						console.error("Tenant init failed !")
					})
			else next()
		} else {
			// If there is no tenantId, redirect to demo_uat
			if (to.params.tenantId) {
				// NOTE: user is logged out if link is changed
				// Store entered link into window.PATH
				// Magic happens in AuthLogin.vue
				window.PATH = to.path
				next(`/${to.params.tenantId}/login`)
			} else next("/demo_uat/login")
		}
	}
})

// Global guard for features
router.beforeEach((to, from, next) => {
	// If settings is not in store yet, skip guarding
	// NOTE:
	// this guarding works only partly, if you come directly to URL
	// at that point settings is not loaded so it will open even disabled features
	if (!store.state.settings) {
		next()
		return
	}

	// Check if this client can access particular feature
	if (
		store.state.settings.features.find(
			feature => feature.id === to.meta.featureId
		) ||
		!to.meta.featureId
	)
		next()
	else next(store.state.settings.features[0].slug)
})

// Global guard to clear store message after route change
router.afterEach((to, from) => {
	if (to.matched.length === 0 || from.matched.length === 0) return

	// NOTE: For Child routes , Parents meta field is accessible via route.matched
	// featureId of route being navigated to
	const toRoute = to.matched[0].meta.featureId
	// featureId of route being navigated away from
	const fromRoute = from.matched[0].meta.featureId

	// Compare routes whether they have different featureId
	// If different routes, flush message in global store after route change
	if (toRoute !== fromRoute) store.commit("REMOVE_MESSAGE", null)
})

export default router
